import { BoxProps, Flex } from '@chakra-ui/react'
import { ContentNodeUtils } from '@helo/cms'
import { Assert, Value, pipe } from '@helo/utils'
import { useMemo } from 'react'

import { FilterEditor } from '../../components/FilterEditor/FilterEditor/FilterEditor'
import { useAppStore } from '../../store/appStore/appStore'
import { useWidgetQuery } from '../widget-query/useWidgetQuery'
import { createWidget } from '../widget-utils'
import {
  FilterFieldAliases,
  FilterFieldSource,
  getTargetFilterFieldKeys,
  prepareCubeDashboardFilterUpdate,
} from './FilterEditorWidget.parts'

export type FilterEditorQueryRef = {
  queryId: string
}

export type FilterEditorWidgetProps = BoxProps & {
  queryId?: string
  /**
   * When provided, the first entry is the source query whose filter fields are
   * rendered; the applied filter value is fanned out to the remaining target
   * queries so a single shared filter drives every dashboard widget.
   */
  queries?: FilterEditorQueryRef[]
  /**
   * Source filter key → acceptable target field keys, for target queries that
   * expose the same semantic field under a different name. Configured in the
   * view config alongside `queries` so the widget stays datasource-agnostic.
   */
  fieldAliases?: FilterFieldAliases
  isLoading?: boolean
  queryVariableName?: string
}

export const FilterEditorWidget = ({
  queryId,
  queries,
  fieldAliases,
  isLoading,
  queryVariableName,
  ...props
}: FilterEditorWidgetProps) => {
  const primaryQueryId = queryId ?? queries?.[0]?.queryId ?? ''
  const variableName = queryVariableName ?? 'filter'

  const { queryNode, updateQueryInputValue, resetPagination, paginated } =
    useWidgetQuery({
      queryId: primaryQueryId,
    })

  const getQuery = useAppStore(store => store.queryStore.getQuery)
  const updateVariables = useAppStore(store => store.queryStore.updateVariables)

  const targetRefs = useMemo(
    () => (queries ?? []).filter(entry => entry.queryId !== primaryQueryId),
    [queries, primaryQueryId],
  )

  const filterInputValueContentNode = useMemo(() => {
    return pipe(
      queryNode.queryVariableContentNodes,
      ContentNodeUtils.findContentNode([['key', variableName]]),
      Assert.orReturn(Value.isNotNullable, () => null),
      Assert.orReturn(ContentNodeUtils.isKind('INPUT_VALUE'), () => null),
    )
  }, [queryNode.queryVariableContentNodes, variableName])

  if (!filterInputValueContentNode) {
    return null
  }

  return (
    <Flex data-testid="FilterEditorWidget" {...props}>
      <FilterEditor
        inputValue={filterInputValueContentNode}
        onChange={async updatedInputValueContentNode => {
          // Only paginated queries have a `skip` variable; calling resetPagination
          // on a non-paginated query (e.g. Cube dashboard queries) throws.
          if (paginated) resetPagination()

          const targets = targetRefs.flatMap(ref => {
            const targetNode = getQuery(ref.queryId) as
              | FilterFieldSource
              | undefined
            if (!targetNode) return []
            return [
              {
                queryId: ref.queryId,
                variableName,
                targetKeys: getTargetFilterFieldKeys(targetNode, variableName),
              },
            ]
          })

          const { sanitizedFilter, targetFilters } =
            prepareCubeDashboardFilterUpdate(
              (updatedInputValueContentNode.data ?? {}) as Record<
                string,
                unknown
              >,
              targets,
              fieldAliases,
            )

          const sanitizedNode = ContentNodeUtils.createContentNodeWithSelection(
            updatedInputValueContentNode.getDataTypeConfig(),
            sanitizedFilter,
          ) as typeof updatedInputValueContentNode

          await updateQueryInputValue(sanitizedNode)

          await Promise.all(
            targetFilters.map(
              ({ queryId: targetId, variableName: name, filter }) =>
                updateVariables(targetId, { [name]: filter }),
            ),
          )
        }}
      />
    </Flex>
  )
}

export const FilterEditorWidgetDefinition = createWidget(FilterEditorWidget, {
  type: 'FilterEditor',
  internalLoading: true,
})
